import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { fmtZMW, fmtDate, exportCSV, can } from "@/lib/helpers";
import { useAuth } from "@/contexts/AuthContext";
import {
  PageHeader, MetricCard, BtnPrimary, BtnSecondary, BtnGhost,
  Modal, Field, Input, Select, Textarea, Empty,
} from "@/components/primitives";
import { toast } from "sonner";
import { Plus, DownloadSimple, GasPump, Trash } from "@phosphor-icons/react";
import MicDictate from "@/components/MicDictate";

const today = () => new Date().toISOString().slice(0, 10);

const BLANK = {
  truck_id: "", log_date: today(), fuel_type: "diesel",
  litres: 0, cost_zmw: 0, odometer_km: 0,
  driver: "", station: "", project_code: "", notes: "",
};

export default function FuelLogs() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [filterTruck, setFilterTruck] = useState("");
  const canEdit = can(user.role, "staff");
  const canDelete = can(user.role, "manager");

  async function load() {
    try {
      const params = filterTruck ? `?truck_id=${filterTruck}` : "";
      const [logs, sum] = await Promise.all([
        api.get(`/fuel-logs${params}`),
        api.get("/fuel-logs/summary?days=30"),
      ]);
      setRows(logs.data); setSummary(sum.data);
    } catch (e) { toast.error(formatError(e)); }
  }
  useEffect(() => { load(); }, [filterTruck]);
  useEffect(() => {
    (async () => {
      try {
        const [t, p] = await Promise.all([
          api.get("/trucks"),
          api.get("/projects?active_only=true"),
        ]);
        setTrucks(t.data); setProjects(p.data);
      } catch (e) { if (process.env.NODE_ENV !== "production") console.debug("non-fatal:", e); }
    })();
  }, []);

  function openCreate() {
    setForm({ ...BLANK, truck_id: trucks[0]?.id || "", odometer_km: trucks[0]?.odometer_km || 0 });
    setOpen(true);
  }
  function pickTruck(id) {
    const t = trucks.find((x) => x.id === id);
    setForm({ ...form, truck_id: id, odometer_km: t?.odometer_km || form.odometer_km });
  }
  async function save(e) {
    e.preventDefault();
    try {
      await api.post("/fuel-logs", {
        ...form,
        log_date: new Date(form.log_date).toISOString(),
        litres: Number(form.litres),
        cost_zmw: Number(form.cost_zmw),
        odometer_km: Number(form.odometer_km),
        project_code: form.project_code || null,
      });
      toast.success("Fuel logged"); setOpen(false); load();
    } catch (e) { toast.error(formatError(e)); }
  }
  async function remove(r) {
    if (!window.confirm(`Delete fuel log of ${r.litres}L on ${fmtDate(r.log_date)}?`)) return;
    try { await api.delete(`/fuel-logs/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatError(e)); }
  }

  return (
    <div data-testid="fuel-page">
      <PageHeader
        overline="Module / Fleet"
        title="Fuel & Diesel"
        subtitle="Log every fuel-up. Costs roll up to truck and project automatically."
        actions={
          <>
            <BtnSecondary onClick={() => exportCSV(rows, `fuel-${Date.now()}.csv`)}>
              <DownloadSimple size={14} weight="bold" className="inline mr-1.5" /> CSV
            </BtnSecondary>
            {canEdit && (
              <BtnPrimary onClick={openCreate} data-testid="fuel-create">
                <Plus size={14} weight="bold" className="inline mr-1.5" /> Log Fuel
              </BtnPrimary>
            )}
          </>
        }
      />

      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          <MetricCard label="30-day litres" value={`${summary.total_litres.toLocaleString()} L`} accent="primary" />
          <MetricCard label="30-day cost" value={fmtZMW(summary.total_cost_zmw)} accent="warning" />
          <MetricCard label="Trucks fuelled" value={summary.by_truck.length} delta={`${rows.length} logs`} deltaLabel="Total" accent="default" />
        </div>
      )}

      <div className="phizo-card p-3 mb-4 flex flex-wrap gap-3 items-center">
        <span className="phizo-label">Filter Truck</span>
        <select
          data-testid="fuel-filter-truck"
          value={filterTruck}
          onChange={(e) => setFilterTruck(e.target.value)}
          className="border border-[#e4e4e7] bg-white px-3 py-1.5 text-sm">
          <option value="">All</option>
          {trucks.map((t) => <option key={t.id} value={t.id}>{t.plate}</option>)}
        </select>
      </div>

      <div className="phizo-card">
        {rows.length === 0 ? <div className="p-5"><Empty label="No fuel logs yet." /></div> : (
          <div className="overflow-x-auto">
            <table className="phizo-table">
              <thead>
                <tr>
                  <th>Date</th><th>Truck</th><th>Type</th>
                  <th className="text-right">Litres</th>
                  <th className="text-right">Cost</th>
                  <th className="text-right">Odometer</th>
                  <th>Project</th><th>Driver</th><th>Station</th><th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    data-testid={`fuel-row-${r.id}`}
                    onClick={() => nav(`/fuel-logs/${r.id}`)}
                    className="cursor-pointer hover:bg-[#f4f4f5]"
                  >
                    <td className="text-sm">{fmtDate(r.log_date)}</td>
                    <td className="mono font-semibold">{r.truck_plate}</td>
                    <td className="text-xs uppercase tracking-wider">{r.fuel_type}</td>
                    <td className="text-right mono font-semibold">{r.litres} L</td>
                    <td className="text-right mono">{fmtZMW(r.cost_zmw)}</td>
                    <td className="text-right mono text-sm">{Number(r.odometer_km).toLocaleString()}</td>
                    <td className="mono text-xs text-[#52525b]">{r.project_code || "—"}</td>
                    <td className="text-sm">{r.driver || "—"}</td>
                    <td className="text-sm text-[#52525b]">{r.station || "—"}</td>
                    <td className="text-right" onClick={(e) => e.stopPropagation()}>
                      {canDelete && (
                        <BtnGhost onClick={() => remove(r)} className="text-[#FF2A2A]">
                          <Trash size={12} weight="bold" />
                        </BtnGhost>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Log Fuel / Diesel" wide>
        <form onSubmit={save} className="grid grid-cols-2 gap-3">
          <Field label="Truck">
            <Select required value={form.truck_id} onChange={(e) => pickTruck(e.target.value)} data-testid="fuel-truck">
              <option value="">Select truck…</option>
              {trucks.map((t) => <option key={t.id} value={t.id}>{`${t.plate} — ${t.make} ${t.model}`}</option>)}
            </Select>
          </Field>
          <Field label="Date">
            <Input type="date" required value={form.log_date}
              onChange={(e) => setForm({ ...form, log_date: e.target.value })} data-testid="fuel-date" />
          </Field>
          <Field label="Fuel Type">
            <Select value={form.fuel_type} onChange={(e) => setForm({ ...form, fuel_type: e.target.value })}>
              <option value="diesel">diesel</option>
              <option value="petrol">petrol</option>
            </Select>
          </Field>
          <Field label="Litres">
            <Input type="number" step="0.1" min="0" required data-testid="fuel-litres"
              value={form.litres} onChange={(e) => setForm({ ...form, litres: e.target.value })} />
          </Field>
          <Field label="Cost (ZMW)">
            <Input type="number" step="0.01" min="0" required data-testid="fuel-cost"
              value={form.cost_zmw} onChange={(e) => setForm({ ...form, cost_zmw: e.target.value })} />
          </Field>
          <Field label="Odometer at Fuel-up (km)">
            <Input type="number" required value={form.odometer_km}
              onChange={(e) => setForm({ ...form, odometer_km: e.target.value })} />
          </Field>
          <Field label="Driver">
            <Input value={form.driver} onChange={(e) => setForm({ ...form, driver: e.target.value })} />
          </Field>
          <Field label="Station">
            <Input value={form.station} onChange={(e) => setForm({ ...form, station: e.target.value })} />
          </Field>
          <Field label="Project (optional)">
            <Select value={form.project_code} onChange={(e) => setForm({ ...form, project_code: e.target.value })}>
              <option value="">—</option>
              {projects.map((p) => <option key={p.code} value={p.code}>{`${p.code} — ${p.name}`}</option>)}
            </Select>
          </Field>
          <div className="col-span-2">
            <div className="flex items-center justify-between mb-1.5">
              <label className="phizo-label">Notes</label>
              <MicDictate
                context="fuel log notes; diesel; litres"
                onTranscript={(text) =>
                  setForm((f) => ({ ...f, notes: (f.notes ? f.notes + " " : "") + text }))
                }
                testId="fuel-notes-mic"
              />
            </div>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="fuel-notes" />
          </div>
          <div className="col-span-2 flex justify-end gap-2 pt-3 border-t border-[#e4e4e7]">
            <BtnSecondary type="button" onClick={() => setOpen(false)}>Cancel</BtnSecondary>
            <BtnPrimary type="submit" data-testid="fuel-save">Save</BtnPrimary>
          </div>
        </form>
      </Modal>
    </div>
  );
}
